import { useState, useEffect, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Loader2, ChevronDown, Search } from 'lucide-react'
import { countryCodes } from '@/lib/data/country-codes'
import { cn } from '@/lib/utils'
import { AvatarUpload } from '@/components/ui/avatar-upload'
import { TranslatableText } from '@/components/TranslatableText'
import { getDynamicTimezones } from '@/utils/dynamicTimezone'
import { LANGUAGES } from '@/lib/constants/languages'
import type { User as UserType } from '@/types/entities'

// Helper function to check if user is an administrator
function isAdministrator(user: UserType | null): boolean {
    return user?.roles?.some(role => role.name === 'admin') || false
}
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { type UserFormData } from './schema'
import { useRoles } from './hooks'
function PhoneCodePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    const { t } = useTranslation()
    const { i18n: i18nInstance } = useTranslation()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const selectedCc = countryCodes.find((cc) => cc.dialCode === value)
    const selectedLabel = selectedCc
        ? `${selectedCc.dialCode} ${t(`userForm.countries.${selectedCc.code}`, { defaultValue: selectedCc.country })}`
        : `+44 ${t('userForm.countries.GB', 'United Kingdom')}`
    const sortedByCountry = useMemo(() => {
        return [...countryCodes]
            .map((cc) => ({
                ...cc,
                label: t(`userForm.countries.${cc.code}`, { defaultValue: cc.country }),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, i18nInstance.language || 'en'))
    }, [t, i18nInstance.language])
    const filtered = useMemo(() => {
        if (!search.trim()) return sortedByCountry
        const q = search.trim().toLowerCase()
        return sortedByCountry.filter(
            (cc) =>
                cc.dialCode.toLowerCase().includes(q) ||
                cc.label.toLowerCase().includes(q) ||
                cc.code.toLowerCase().includes(q)
        )
    }, [sortedByCountry, search])
    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch('') }}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-10 w-[200px] shrink-0 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
                        !value && 'text-muted-foreground'
                    )}
                >
                    <span className="truncate">{selectedLabel}</span>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <div className="flex items-center border-b px-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t('userForm.phoneCodePlaceholder', 'Search country...')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-9 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        autoFocus
                    />
                </div>
                <div className="max-h-64 overflow-auto p-1">
                    {filtered.length === 0 ? (
                        <div className="py-4 text-center text-sm text-muted-foreground">
                            {t('userForm.phoneCodeNoResults', 'No countries match your search')}
                        </div>
                    ) : (
                        filtered.map((cc) => {
                            const optionLabel = `${cc.dialCode} ${cc.label}`
                            const isSelected = value === cc.dialCode
                            return (
                                <button
                                    key={cc.code}
                                    type="button"
                                    className={cn(
                                        'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                                        isSelected && 'bg-accent text-accent-foreground'
                                    )}
                                    onClick={() => {
                                        onChange(cc.dialCode)
                                        setOpen(false)
                                    }}
                                >
                                    {optionLabel}
                                </button>
                            )
                        })
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}
import { useTeams } from '@/features/teams/hooks'
import { UserTeamsSelectionDialog } from './components/UserTeamsSelectionDialog'
import { UserRolesSelectionDialog } from './components/UserRolesSelectionDialog'
import { Button } from '@/components/ui/button'
import { Users, Shield } from 'lucide-react'
import { locationsApi } from '@/features/locations/api'
import { useQuery } from '@tanstack/react-query'

interface UserFormProps {
    form: UseFormReturn<UserFormData>
    isEditing?: boolean
    user?: UserType | null
    isUploadingAvatar?: boolean
    setIsUploadingAvatar?: (uploading: boolean) => void
}

export function UserForm({ form, isEditing = false, user, isUploadingAvatar = false, setIsUploadingAvatar }: UserFormProps) {
    const { t } = useTranslation()
    const { i18n: i18nInstance } = useTranslation()
    const [_avatarFile, setAvatarFile] = useState<File | null>(null)
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
    const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(null)
    const [currentAvatarColor, setCurrentAvatarColor] = useState<string | null>(null)
    const [showTeamsDialog, setShowTeamsDialog] = useState(false)
    const [showRolesDialog, setShowRolesDialog] = useState(false)
    const selectedTeams = form.watch('teams') || []

    // Get roles from database
    const { data: rolesResponse } = useRoles()
    const availableRoles = rolesResponse?.data || []
    
    // Get teams from database
    const { data: teamsResponse } = useTeams()
    const availableTeams = teamsResponse?.data || []

    // Get locations from database (same as objects form)
    const { data: locationsData } = useQuery({
        queryKey: ['locations'],
        queryFn: async () => {
            const response = await locationsApi.list({ page: 1, limit: 1000 })
            return response.data.data || []
        },
    })

    // Memoize watched values to prevent unnecessary re-renders
    const watchedName = form.watch('name')
    const memoizedName = useMemo(() => watchedName || 'User', [watchedName])

    // Initialize avatar state when user data loads
    useEffect(() => {
        if (user?.avatarUrl) {
            setCurrentAvatarUrl(user.avatarUrl)
            setAvatarPreview(user.avatarUrl)
        } else {
            setCurrentAvatarUrl(null)
            setAvatarPreview(null)
        }
        setCurrentAvatarColor(user?.avatarColor || null)
    }, [user?.avatarUrl, user?.avatarColor])

    const handleAvatarChange = async (file: File | null) => {
        setAvatarFile(file)
        if (file) {
            // Upload file to server
            setIsUploadingAvatar?.(true)
            try {
                const formData = new FormData()
                formData.append('avatar', file)
                formData.append('subdirectory', 'avatars')

                // Create an AbortController for timeout
                const controller = new AbortController()
                const timeoutId = setTimeout(() => controller.abort(), 20000) // 20 second timeout

                const response = await fetch('/api/upload', {
                    method: 'POST',
                    body: formData,
                    signal: controller.signal,
                    credentials: 'include' // Include cookies for authentication
                })

                clearTimeout(timeoutId)

                if (response.ok) {
                    const result = await response.json()
                    // Update both local state and form values (same as SimpleEditProfileDialog)
                    setCurrentAvatarUrl(result.image.url)
                    setAvatarPreview(result.image.url)
                    setCurrentAvatarColor(null) // Clear color when image is uploaded
                    form.setValue('avatarUrl', result.image.url)
                    form.setValue('avatarColor', null)
                } else {
                    const errorText = await response.text()
                    console.error('Failed to upload avatar:', response.status, errorText)
                    alert(`Failed to upload avatar: ${response.status} ${errorText}`)
                }
            } catch (_error) {
                console.error('Error uploading avatar:', _error)
                if (_error.name === 'AbortError') {
                    alert('Upload timed out. Please try again.')
                } else {
                    alert(`Error uploading avatar: ${_error.message}`)
                }
            } finally {
                setIsUploadingAvatar?.(false)
            }
        } else {
            // Remove avatar - clear both URL and color (same as SimpleEditProfileDialog)
            setCurrentAvatarUrl(null)
            setAvatarPreview(null)
            setCurrentAvatarColor(null)
            form.setValue('avatarUrl', null)
            form.setValue('avatarColor', null)
        }
    }

    const handleColorChange = (color: string) => {
        // Update both local state and form values (same as SimpleEditProfileDialog)
        setCurrentAvatarColor(color)
        setCurrentAvatarUrl(null) // Clear avatar URL when color is selected
        setAvatarPreview(null)
        form.setValue('avatarColor', color)
        form.setValue('avatarUrl', null)
    }

    return (
        <div className="space-y-6">
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Profile Picture, Name, Email, Phone, Active */}
                <div className="space-y-4">
                    {/* Profile Picture */}
                    <div className="space-y-2">
                        <Label>{t('userForm.profilePicture')}</Label>
                        {isUploadingAvatar && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {t('userForm.uploading')}
                            </div>
                        )}
                        <AvatarUpload
                            name={memoizedName}
                            avatarUrl={avatarPreview || currentAvatarUrl}
                            avatarColor={currentAvatarColor}
                            userId={user?.id}
                            onAvatarChange={handleAvatarChange}
                            onColorChange={handleColorChange}
                            disabled={isUploadingAvatar || !!user?.is_system}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="name">{t('userForm.name')}</Label>
                        <Input
                            id="name"
                            placeholder={t('userForm.namePlaceholder')}
                            {...form.register('name')}
                            disabled={user?.is_system}
                        />
                        {form.formState.errors.name && (
                            <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
                        )}
                    </div>

                    {/* Phone (dial code + number) - searchable, alphabetical by country in user's language */}
                    <FormField
                        control={form.control}
                        name="phoneCode"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('userForm.phone', 'Phone')} *</FormLabel>
                                <div className="flex gap-2 items-center">
                                    <FormControl>
                                        <PhoneCodePicker
                                            value={field.value ?? '+44'}
                                            onChange={(v) => field.onChange(v || '+44')}
                                        />
                                    </FormControl>
                                    <FormField
                                        control={form.control}
                                        name="phoneNumber"
                                        render={({ field: phoneField }) => (
                                            <FormControl>
                                                <div className="flex-1">
                                                    <Input
                                                        type="tel"
                                                        required
                                                        aria-required="true"
                                                        placeholder={t('userForm.phoneNumberPlaceholder', 'Phone number')}
                                                        {...phoneField}
                                                        value={phoneField.value ?? ''}
                                                    />
                                                </div>
                                            </FormControl>
                                        )}
                                    />
                                </div>
                                <FormMessage />
                                {/* The number's own error (the field is nested inside the country code's). */}
                                {form.formState.errors.phoneNumber && (
                                    <p className="text-sm font-medium text-destructive" role="alert" data-testid="phone-error">
                                        {String(form.formState.errors.phoneNumber.message)}
                                    </p>
                                )}
                            </FormItem>
                        )}
                    />

                    <div className="space-y-2">
                        <Label htmlFor="email">{t('userForm.email')}</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder={t('userForm.emailPlaceholder')}
                            autoComplete="off"
                            {...form.register('email')}
                            disabled={user?.is_system}
                        />
                        {form.formState.errors.email && (
                            <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                        )}
                    </div>

                    <FormField
                        control={form.control}
                        name="language"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('users.language')}</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value} disabled={user?.is_system}>
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('users.language')} />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {LANGUAGES.map((lang) => (
                                            <SelectItem key={lang.code} value={lang.code}>
                                                {lang.flag} {lang.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {!isEditing && (
                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('userForm.password')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="password"
                                            placeholder={t('userForm.passwordPlaceholder')}
                                            autoComplete="new-password"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    )}

                    {/* Active toggle moved to bottom of left column */}
                    <FormField
                        control={form.control}
                        name="isActive"
                        render={({ field }) => {
                            const isSystemUser = user?.is_system || false

                            return (
                                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                    <div className="space-y-0.5">
                                        <FormLabel className="text-base">{t('userForm.active')}</FormLabel>
                                        <div className="text-sm text-muted-foreground">
                                            {isSystemUser
                                                ? t('userForm.systemAccountCannotDisable')
                                                : t('userForm.enableDisableAccount')}
                                        </div>
                                    </div>
                                    <FormControl>
                                        <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                            disabled={isSystemUser}
                                        />
                                    </FormControl>
                                </FormItem>
                            )
                        }}
                    />

                </div>

                {/* Right Column - Timezone / Location / Roles / Teams / MFA / Standby */}
                <div className="space-y-4">
                    <FormField
                        control={form.control}
                        name="timezone"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('users.timezone')}</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value} disabled={user?.is_system}>
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('users.timezone')} />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {getDynamicTimezones().map((tz) => (
                                            <SelectItem key={tz.value} value={tz.value}>
                                                {tz.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="location"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('users.location')} *</FormLabel>
                                <Select
                                    value={field.value?.toString() || ''}
                                    onValueChange={(value) => {
                                        field.onChange(value ? parseInt(value) : null)
                                    }}
                                    disabled={user?.is_system}
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('users.selectLocation')} />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {locationsData?.map((location: any) => (
                                            <SelectItem key={location.id} value={location.id.toString()}>
                                                {location.description}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="roles"
                        render={({ field }) => {
                            const selectedRoleNames = availableRoles
                                .filter((role) => field.value?.includes(role.id))
                                .map((role) => i18nInstance.exists(`roles.${role.name}`) ? t(`roles.${role.name}`) : role.name)

                            return (
                                <FormItem>
                                    <div className="mb-2">
                                        <FormLabel className="text-base">{t('users.roles')}</FormLabel>
                                        <p className="text-sm text-muted-foreground">
                                            {t('userForm.selectAtLeastOneRole')}
                                        </p>
                                    </div>
                                    <FormControl>
                                        <div className="space-y-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="w-full justify-start"
                                                onClick={() => setShowRolesDialog(true)}
                                                disabled={user?.is_system}
                                            >
                                                <Shield className="mr-2 h-4 w-4" />
                                                {field.value && field.value.length > 0
                                                    ? t('users.selectedRolesCount', { count: field.value.length })
                                                    : t('users.selectRolesButton')}
                                            </Button>
                                            {selectedRoleNames.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {selectedRoleNames.slice(0, 3).map((name, index) => {
                                                        const role = availableRoles.find(
                                                            (r) => (i18nInstance.exists(`roles.${r.name}`) ? t(`roles.${r.name}`) : r.name) === name
                                                        )
                                                        return (
                                                            <span
                                                                key={role?.id || index}
                                                                className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-secondary text-secondary-foreground"
                                                            >
                                                                {name}
                                                            </span>
                                                        )
                                                    })}
                                                    {selectedRoleNames.length > 3 && (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-secondary text-secondary-foreground">
                                                            +{selectedRoleNames.length - 3} {t('users.more')}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                    <UserRolesSelectionDialog
                                        open={showRolesDialog}
                                        onOpenChange={setShowRolesDialog}
                                        selectedRoleIds={field.value || []}
                                        onSelectionChange={(roleIds) => {
                                            field.onChange(roleIds)
                                        }}
                                        disabled={user?.is_system}
                                    />
                                </FormItem>
                            )
                        }}
                    />

                    <FormField
                        control={form.control}
                        name="teams"
                        render={({ field }) => {
                            const selectedTeamNames = availableTeams
                                .filter((team) => field.value?.includes(team.id))
                                .map((team) => team.teamName)

                            return (
                                <FormItem>
                                    <div className="mb-2">
                                        <FormLabel className="text-base">{t('users.teams')}</FormLabel>
                                        <p className="text-sm text-muted-foreground">
                                            {t('userForm.selectTeams')}
                                        </p>
                                    </div>
                                    <FormControl>
                                        <div className="space-y-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="w-full justify-start"
                                                onClick={() => setShowTeamsDialog(true)}
                                                disabled={user?.is_system}
                                            >
                                                <Users className="mr-2 h-4 w-4" />
                                                {field.value && field.value.length > 0
                                                    ? t('users.selectedTeamsCount', { count: field.value.length })
                                                    : t('users.selectTeamsButton')}
                                            </Button>
                                            {selectedTeamNames.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {selectedTeamNames.slice(0, 3).map((name, index) => {
                                                        const team = availableTeams.find(
                                                            (t) => t.teamName === name
                                                        )
                                                        return (
                                                            <span
                                                                key={team?.id || index}
                                                                className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-secondary text-secondary-foreground"
                                                            >
                                                                {name}
                                                            </span>
                                                        )
                                                    })}
                                                    {selectedTeamNames.length > 3 && (
                                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-secondary text-secondary-foreground">
                                                            +{selectedTeamNames.length - 3} {t('users.more')}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                    {form.formState.errors.primaryTeamId && (
                                        <p className="text-sm text-destructive mt-1">
                                            {form.formState.errors.primaryTeamId.message}
                                        </p>
                                    )}
                                    <UserTeamsSelectionDialog
                                        open={showTeamsDialog}
                                        onOpenChange={setShowTeamsDialog}
                                        selectedTeamIds={field.value || []}
                                        primaryTeamId={form.watch('primaryTeamId')}
                                        onSelectionChange={(teamIds, primaryTeamId) => {
                                            field.onChange(teamIds)
                                            // Ensure primary team is set - if teams selected but no primary, use first team
                                            let finalPrimaryId = primaryTeamId
                                            if (teamIds.length > 0) {
                                                if (!finalPrimaryId || !teamIds.includes(finalPrimaryId)) {
                                                    // If no primary selected or primary is not in selected teams, use first team
                                                    finalPrimaryId = teamIds[0]
                                                }
                                            } else {
                                                finalPrimaryId = null
                                            }
                                            form.setValue('primaryTeamId', finalPrimaryId, { shouldValidate: true })
                                            // Clear any errors
                                            form.clearErrors('primaryTeamId')
                                        }}
                                        disabled={user?.is_system}
                                    />
                                </FormItem>
                            )
                        }}
                    />

                    {/* No second-factor switches here (14 Oct 2026). A second factor is required of every
                        user; they choose the kind when they set up their first device with an enrolment
                        code, and an administrator takes a device away under "Sign-in devices". */}
                    {/* Standby toggle moved to bottom of right column, under MFA */}
                    <FormField
                        control={form.control}
                        name="standby"
                        render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                <div className="space-y-0.5">
                                    <FormLabel className="text-base">{t('userForm.standby')}</FormLabel>
                                    <div className="text-sm text-muted-foreground">
                                        {t('userForm.standbyDescription')}
                                    </div>
                                </div>
                                <FormControl>
                                    <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </div>
            </div>
        </div>
    )
}